import { RubyGemMark } from '../../components/RubyGemMark';
import { useEffect, useRef, useState } from 'react';
import { Plus, CheckCircle2, Check, ChevronDown, Package, Eye } from 'lucide-react';
import { academyApi, type ModuleWithChapters } from '../../services/academyApi';
import { categoryOf, groupModulesByCategory, UNCATEGORISED, type AcademyChapter, type AcademyModule } from '@platform/shared-types';
import ChapterEditor from './ChapterEditor';
import ModulePackagesField from './ModulePackagesField';
import ModuleVisibilityField, { visibilitySummary, type ModuleStatus } from './ModuleVisibilityField';
import { api } from '../../services/apiService';
import { RubyLoader } from '../../components/RubyLoader';
import { createSerialQueue, patchModule, saveSynced, updateChapters, type Enqueue, type ModuleAudience } from './moduleWrites';

const COLLAPSED_STORAGE_KEY = 'academy.collapsedCategories';

/**
 * Runs a write. Writes are SERIALISED (never concurrent) and never block the
 * form, so typing is never interrupted by an in-flight save.
 * (Defined in moduleWrites.ts; re-exported for ChapterEditor/QuestionEditor.)
 */
export type { Enqueue };

/** Late-bound so the saveModule in use is always the live academyApi one. */
const save: typeof academyApi.saveModule = (id, module, chapters) => academyApi.saveModule(id, module, chapters);

export default function CurriculumTab() {
  const [modules, setModules] = useState<ModuleWithChapters[]>([]);
  const [selected, setSelected] = useState<AcademyChapter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingCount, setSavingCount] = useState(0);
  const [savedAt, setSavedAt] = useState(0);
  const [titleDrafts, setTitleDrafts] = useState<Record<string, string>>({});
  const [categoryDrafts, setCategoryDrafts] = useState<Record<string, string>>({});
  /** Package links being edited, per module -- so two quick toggles never compute from a stale list. */
  const [packageDrafts, setPackageDrafts] = useState<Record<string, string[]>>({});
  const [packagesOpen, setPackagesOpen] = useState<string | null>(null);
  /** Audience / status being saved, per module -- same draft pattern as packageDrafts. */
  const [visDrafts, setVisDrafts] = useState<Record<string, { audience?: ModuleAudience[]; status?: ModuleStatus }>>({});
  const [visibilityOpen, setVisibilityOpen] = useState<string | null>(null);
  /** For the "Who will see it" count. `undefined` = not loaded (or failed) -> the field shows "—". */
  const [tenants, setTenants] = useState<Array<{ id: string; enabledPackages?: string[] }> | undefined>(undefined);
  const busy = savingCount > 0;

  /** Collapsed category names, persisted per-viewer. Never lets a private window or blocked site data break the page. */
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_STORAGE_KEY);
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch { return new Set(); }
  });

  const toggleCategory = (category: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category); else next.add(category);
      try { localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  /**
   * Latest modules, readable synchronously inside a queued write. A write must
   * build its payload from the CURRENT array at the moment it runs — not from
   * the render closure it was created in — because `saveModule` replaces a
   * module's whole chapter set, so a payload built from a stale snapshot would
   * roll back whatever landed in between.
   *
   * The ref is the SOURCE OF TRUTH and `modules` state is a copy of it. Every
   * save writes its result into the ref before resolving (`saveSynced`), so
   * the next queued write -- which runs one microtask later, before any
   * render -- already sees it. State is never copied back into the ref: a
   * late render would otherwise overwrite a newer ref with an older array.
   */
  const modulesRef = useRef<ModuleWithChapters[]>([]);

  /**
   * Serialises writes. Two overlapping whole-array PUTs would race and the
   * loser's changes would vanish, so each waits for the previous to settle.
   * This replaces the old approach of disabling every field while saving,
   * which silently dropped edits: a field disabled mid-typing loses focus
   * WITHOUT firing blur, so its pending change was never saved.
   */
  const serial = useRef<Enqueue>(createSerialQueue());
  const enqueue: Enqueue = (fn) => {
    const run = serial.current(fn);
    setSavingCount((n) => n + 1);
    return run.then(
      (v) => { setSavingCount((n) => n - 1); setSavedAt(Date.now()); return v; },
      (e) => { setSavingCount((n) => n - 1); throw e; },
    );
  };

  /** Re-renders from the ref (already spliced by saveSynced) — no refetch, so the page never blanks. */
  const syncFromRef = () => setModules(modulesRef.current);

  useEffect(() => {
    void (async () => {
      try {
        const { modules } = await academyApi.listModules();
        modulesRef.current = modules;
        setModules(modules);
      } catch (e: any) {
        setError(e.message || 'Could not load the curriculum.');
      } finally {
        setLoading(false);
      }
    })();
    // Non-blocking: only feeds the packages reach count.
    api.tenants.getAll()
      .then((list) => setTenants(Array.isArray(list) ? list : undefined))
      .catch(() => setTenants(undefined));
  }, []);

  const write = async (fn: () => Promise<ModuleWithChapters>, failure: string) => {
    try {
      await enqueue(fn);
      syncFromRef();
      setError('');
    } catch (e: any) {
      setError(e.message || failure);
    }
  };

  const addModule = () =>
    write(
      () => {
        const order = modulesRef.current.length;
        const module: Partial<AcademyModule> = {
          title: `Module ${order + 1}`, summary: '', order,
          audience: ['affiliate'], status: 'draft',
        };
        const id = crypto.randomUUID();
        return saveSynced(modulesRef, save, id, module, []);
      },
      'Could not create the module.',
    );

  /**
   * Renames a module. `saveModule` replaces the whole chapter set, so the
   * module's existing chapters are passed straight back through — otherwise a
   * rename would silently delete every chapter under it.
   */
  const renameModule = (moduleId: string, nextTitle: string) => {
    const title = nextTitle.trim();
    const current = modulesRef.current.find((m) => m.module.moduleId === moduleId);
    setTitleDrafts((d) => { const { [moduleId]: _drop, ...rest } = d; return rest; });
    if (!current || !title || title === current.module.title) return;
    return write(
      () => patchModule(modulesRef, save, moduleId, { title }),
      'Could not rename the module.',
    );
  };

  /**
   * Sets a module's category. Mirrors `renameModule` exactly: same
   * `modulesRef` read, same chapter pass-through (otherwise the save would
   * delete every chapter under the module), same enqueue.
   */
  const saveCategory = (moduleId: string, nextCategory: string) => {
    const category = nextCategory.trim();
    const current = modulesRef.current.find((m) => m.module.moduleId === moduleId);
    setCategoryDrafts((d) => { const { [moduleId]: _drop, ...rest } = d; return rest; });
    if (!current || category === (current.module.category ?? '')) return;
    return write(
      () => patchModule(modulesRef, save, moduleId, { category }),
      'Could not update the category.',
    );
  };

  /**
   * Sets a module's linked packages. Same shape as `saveCategory`: the payload
   * is built from `modulesRef` at run time and the chapters pass straight
   * through. The draft holds the latest list so rapid toggles compose; each
   * queued write sends the whole list, so the last one wins. On success or
   * failure the draft is dropped (unless superseded) and the field shows the
   * saved value.
   */
  const savePackages = async (moduleId: string, packageIds: string[]) => {
    setPackageDrafts((d) => ({ ...d, [moduleId]: packageIds }));
    try {
      await enqueue(() => patchModule(modulesRef, save, moduleId, { packageIds }));
      syncFromRef();
      // Drop the draft only if no newer toggle replaced it, so the chips then
      // show what the server saved.
      setPackageDrafts((d) => {
        if (d[moduleId] !== packageIds) return d;
        const { [moduleId]: _drop, ...rest } = d; return rest;
      });
      setError('');
    } catch (e: any) {
      setPackageDrafts((d) => {
        if (d[moduleId] !== packageIds) return d;
        const { [moduleId]: _drop, ...rest } = d; return rest;
      });
      setError(e.message || 'Could not update the packages.');
    }
  };

  /**
   * Sets who a module is for, or whether it is live. Same queue + run-time
   * patchModule as savePackages (chapters and every other field pass straight
   * through). The draft shows the choice immediately and is dropped once this
   * write settles, unless a newer toggle of the same field replaced it.
   */
  const saveVisibility = async (moduleId: string, patch: { audience: ModuleAudience[] } | { status: ModuleStatus }) => {
    const field = 'audience' in patch ? 'audience' : 'status';
    const value = 'audience' in patch ? patch.audience : patch.status;
    setVisDrafts((d) => ({ ...d, [moduleId]: { ...d[moduleId], [field]: value } }));
    const drop = () => setVisDrafts((d) => {
      const cur = d[moduleId];
      if (!cur || cur[field] !== value) return d;
      const { [field]: _drop, ...rest } = cur;
      return { ...d, [moduleId]: rest };
    });
    try {
      await enqueue(() => patchModule(modulesRef, save, moduleId, patch));
      syncFromRef();
      drop();
      setError('');
    } catch (e: any) {
      drop();
      setError(e.message || (field === 'audience' ? 'Could not update who the module is for.' : 'Could not change the module status.'));
    }
  };

  const addChapter = async (moduleId: string) => {
    let chapter: AcademyChapter = {
      chapterId: crypto.randomUUID(),
      moduleId,
      title: 'New chapter',
      summary: '',
      order: modulesRef.current.find((m) => m.module.moduleId === moduleId)?.chapters.length ?? 0,
      required: true,
    };
    await write(
      // Order from the chapter set AT RUN TIME, so two quick adds do not share a position.
      () => updateChapters(modulesRef, save, moduleId, (chapters) => {
        chapter = { ...chapter, order: chapters.length };
        return [...chapters, chapter];
      }),
      'Could not add the chapter.',
    );
    setSelected(chapter);
  };

  /** Saves one chapter. Payload is built at run time from the latest modules. */
  const saveChapter = (next: AcademyChapter) =>
    write(
      () => updateChapters(modulesRef, save, next.moduleId,
        (chapters) => chapters.map((c) => (c.chapterId === next.chapterId ? next : c))),
      'That change did not save.',
    );

  if (loading) return <RubyLoader variant="page" label="Loading the curriculum…" />;

  const status = busy
    ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500">
        <RubyGemMark size={12} drawing />Saving…
      </span>
    : savedAt
      ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-400">
          <Check className="w-3 h-3" />Saved
        </span>
      : null;

  // Categories already in use, for the autocomplete list on the category input.
  const categories = Array.from(
    new Set(modules.map((m) => categoryOf(m.module)).filter((c) => c !== UNCATEGORISED)),
  ).sort();

  // Grouped for display; numbering stays keyed off the flat, order-sorted
  // array so `i + 1` still reads as a position in the whole curriculum.
  const groups = groupModulesByCategory(modules);
  const moduleIndex = new Map(modules.map((m, i) => [m.module.moduleId, i]));

  return (
    <div className="grid gap-4 lg:grid-cols-[340px_1fr] items-start">
      <section className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">Modules {status}</h2>
          <button
            type="button"
            onClick={addModule}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-700 transition"
          >
            <Plus className="w-3.5 h-3.5" />
            Module
          </button>
        </div>

        <div className="p-3 space-y-2">
          {modules.length === 0 && (
            <p className="text-sm text-gray-500 p-3">
              No modules yet. Add one, name it, then record a video for its first chapter.
            </p>
          )}

          {groups.map(({ category, items }) => {
            const isCollapsed = collapsed.has(category);
            return (
              <div key={category} className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleCategory(category)}
                  aria-expanded={!isCollapsed}
                  className="w-full flex items-center justify-between gap-2 px-3 min-h-[44px] rounded-lg text-left bg-gray-100 hover:bg-gray-200 transition"
                >
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    {category} <span className="text-gray-400 font-normal normal-case">· {items.length} module{items.length === 1 ? '' : 's'}</span>
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                  />
                </button>

                {!isCollapsed && items.map((m) => {
                  const id = m.module.moduleId;
                  const i = moduleIndex.get(id) ?? 0;
                  const draft = titleDrafts[id] ?? m.module.title;
                  const categoryDraft = categoryDrafts[id] ?? (m.module.category ?? '');
                  const packageIds = packageDrafts[id] ?? m.module.packageIds ?? [];
                  const audience = visDrafts[id]?.audience ?? m.module.audience ?? ['affiliate'];
                  const moduleStatus = visDrafts[id]?.status ?? m.module.status ?? 'draft';
                  return (
                    <div key={id} className="border border-gray-200 rounded-xl bg-white overflow-hidden">
                      <div className="p-3">
                        <div className="flex items-start gap-2">
                          <span className="text-xs font-semibold text-gray-400 pt-3 w-4 flex-shrink-0">
                            {i + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            {/* Editable module name — reads as text until focused. */}
                            <input
                              value={draft}
                              onChange={(e) => setTitleDrafts((d) => ({ ...d, [id]: e.target.value }))}
                              onBlur={(e) => renameModule(id, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                if (e.key === 'Escape') {
                                  setTitleDrafts((d) => { const { [id]: _drop, ...rest } = d; return rest; });
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              aria-label={`Module ${i + 1} name`}
                              placeholder="Name this module"
                              className="w-full min-h-[44px] bg-transparent px-2 rounded-lg text-sm font-semibold text-gray-900 border border-transparent hover:border-gray-300 focus:border-gray-900 focus:bg-white outline-none transition"
                            />
                            {/* Editable module category — autocompletes from categories already in use. */}
                            <input
                              list="academy-categories"
                              value={categoryDraft}
                              onChange={(e) => setCategoryDrafts((d) => ({ ...d, [id]: e.target.value }))}
                              onBlur={(e) => saveCategory(id, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                if (e.key === 'Escape') {
                                  setCategoryDrafts((d) => { const { [id]: _drop, ...rest } = d; return rest; });
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              placeholder="Category — e.g. Sales"
                              aria-label={`Module ${i + 1} category`}
                              className="mt-1 w-full min-h-[44px] bg-transparent px-2 rounded-lg text-xs text-gray-600 border border-transparent hover:border-gray-300 focus:border-gray-900 focus:bg-white outline-none transition"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 pl-6">
                          {m.chapters.length} chapter{m.chapters.length === 1 ? '' : 's'}
                          {moduleStatus === 'published' ? ' · Live' : ' · Draft'}
                        </p>
                        {/* Audience + Draft/Published -- collapsed to a summary until opened. */}
                        <div className="pl-6 mt-1">
                          <button
                            type="button"
                            onClick={() => setVisibilityOpen((o) => (o === id ? null : id))}
                            aria-expanded={visibilityOpen === id}
                            className={`inline-flex items-center gap-1.5 min-h-[44px] px-2 rounded-lg text-xs font-semibold text-left transition ${
                              moduleStatus === 'published' ? 'text-[#9F1239] hover:bg-[#FFF1F3]' : 'text-gray-500 hover:bg-gray-100'
                            }`}
                          >
                            <Eye className="w-3.5 h-3.5 flex-shrink-0" />
                            {visibilitySummary(audience, moduleStatus)}
                            <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${visibilityOpen === id ? '' : '-rotate-90'}`} />
                          </button>
                          {visibilityOpen === id && (
                            <div className="mt-2">
                              <ModuleVisibilityField
                                audience={audience}
                                status={moduleStatus}
                                onAudience={(next) => void saveVisibility(id, { audience: next })}
                                onStatus={(next) => void saveVisibility(id, { status: next })}
                              />
                            </div>
                          )}
                        </div>
                        {/* Linked packages -- collapsed to a count until opened. */}
                        <div className="pl-6 mt-1">
                          <button
                            type="button"
                            onClick={() => setPackagesOpen((o) => (o === id ? null : id))}
                            aria-expanded={packagesOpen === id}
                            className={`inline-flex items-center gap-1.5 min-h-[44px] px-2 rounded-lg text-xs font-semibold transition ${
                              packageIds.length ? 'text-[#9F1239] hover:bg-[#FFF1F3]' : 'text-gray-500 hover:bg-gray-100'
                            }`}
                          >
                            <Package className="w-3.5 h-3.5" />
                            {packageIds.length
                              ? `${packageIds.length} package${packageIds.length === 1 ? '' : 's'} linked`
                              : 'All tenants · link packages'}
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${packagesOpen === id ? '' : '-rotate-90'}`} />
                          </button>
                          {packagesOpen === id && (
                            <div className="mt-2">
                              <ModulePackagesField
                                value={packageIds}
                                onChange={(v) => void savePackages(id, v)}
                                tenants={tenants}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="border-t border-gray-200 p-2 space-y-1 bg-white">
                        {m.chapters.map((c, ci) => {
                          const on = selected?.chapterId === c.chapterId;
                          return (
                            <button
                              key={c.chapterId}
                              type="button"
                              onClick={() => setSelected(c)}
                              className={`w-full text-left flex items-center gap-2 px-3 min-h-[44px] rounded-lg text-sm transition ${
                                on ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                              }`}
                            >
                              <span className={`text-xs w-8 ${on ? 'text-white/50' : 'text-gray-400'}`}>
                                {i + 1}.{ci + 1}
                              </span>
                              <span className="truncate">{c.title}</span>
                              {c.video && (
                                <CheckCircle2
                                  className={`w-3.5 h-3.5 ml-auto flex-shrink-0 ${on ? 'text-white' : 'text-gray-900'}`}
                                />
                              )}
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() => addChapter(id)}
                          className="w-full text-left flex items-center gap-1.5 px-3 min-h-[44px] rounded-lg text-sm text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Chapter
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
          <datalist id="academy-categories">
            {categories.map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>
      </section>

      <section>
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-3">
            {error}
          </p>
        )}
        {selected
          ? (
            <ChapterEditor
              // Remount on chapter switch: `chapter` seeds local state only on
              // mount, so without a key React reuses the same fiber across a
              // chapter switch and the form silently keeps editing (and can
              // overwrite) the PREVIOUS chapter.
              key={selected.chapterId}
              chapter={selected}
              moduleId={selected.moduleId}
              onSave={saveChapter}
              enqueue={enqueue}
              status={status}
            />
          )
          : (
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-8 text-center">
              <p className="text-gray-500 text-sm">
                Pick a chapter to edit its video and questions.
              </p>
            </div>
          )}
      </section>
    </div>
  );
}
